<template>
  <!--
    A real switch, not a bare checkbox.

    Settings screens are read at a glance: "is this on?" has to be answerable
    without reading the word next to the box. It stays a native <input> under
    the paint so the keyboard, the form and the screen reader all keep working.
  -->
  <label class="switch" :class="{ 'switch--disabled': disabled }">
    <input
      :id="controlId"
      type="checkbox"
      class="switch__input"
      :checked="modelValue"
      :disabled="disabled"
      :aria-describedby="describedBy"
      @change="$emit('update:modelValue', $event.target.checked)"
    />
    <span class="switch__track" aria-hidden="true"><span class="switch__thumb" /></span>
    <span v-if="label" class="switch__label">{{ label }}</span>
  </label>
</template>

<script setup>
import { computed, inject } from 'vue'

import { FIELD_ID } from '../utils/fieldId'

const props = defineProps({
  modelValue: Boolean,
  label: String,
  id: String,
  disabled: Boolean,
  describedBy: String,
})
defineEmits(['update:modelValue'])

// El id que pone la etiqueta del campo que lo envuelve, cuando nadie ha dado
// uno: sin esto, pulsar «Trabajo parado» encima del interruptor no lo movia.
const fieldId = inject(FIELD_ID, null)
const controlId = computed(() => props.id ?? fieldId)
</script>

<style scoped>
.switch {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  user-select: none;
}
.switch--disabled { cursor: not-allowed; opacity: 0.55; }

.switch__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.switch__track {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 22px;
  border-radius: var(--radius-full);
  background: var(--color-gray-300);
  transition: background var(--fast);
}

.switch__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: var(--shadow-sm);
  transition: transform var(--base);
}

.switch__input:checked + .switch__track { background: var(--color-primary); }
.switch__input:checked + .switch__track .switch__thumb { transform: translateX(18px); }

/* The ring goes on the track, since the input itself is invisible. */
.switch__input:focus-visible + .switch__track {
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}

.switch__label { font-size: var(--text-sm); }
</style>
